import React from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { Modal, Panel, PanelGroup, designSystem } from '@yola/ws-ui';
import { hdrm } from '@yola/ws-sdk';
import fieldNames from '../constants/field-names';

const {
  ControlGroup,
  TreeSelectField,
  Stack,
  Icon,
  Tag,
  Box,
  RadioButtonGroupField,
  HorizontalStack,
  Paragraph,
  Link,
  ToggleField,
  DialogHeader,
  DialogHeaderTitle,
  DialogButtonGroup,
  DialogButton,
  DialogButtonGroupSeparator,
} = designSystem;

const ATTRIBUTES = {
  [hdrm.constants.attributes.DISABLE_BLUR_ATTR]: true,
};

const DIALOG_WIDTH = 334;
const FLOATING_ELEMENT_SELECTOR = '.ws-ui-tree-select__list';

const requiredValidation = (value) => value.length > 0;
const initialExpandedPageItemIdsDefault = [];

function TranslatePageDialog({
  pageItems,
  languageItems,
  overrideExistingPagesOptions,
  captions,
  control,
  showOverrideExistingPages,
  initialExpandedPageItemIds = initialExpandedPageItemIdsDefault,
  onWebsiteSettingsClick,
  onSubmit,
  onCancel,
}) {
  return (
    <Modal
      width={DIALOG_WIDTH}
      height="auto"
      centered
      resizable={false}
      draggable={false}
      attributes={ATTRIBUTES}
      onPressEsc={onCancel}
      overlay="visible"
      isContainerScrollable
      offsetX={0}
      offsetY={0}
      className="ws-translate-page-dialog"
      floatingModalElementSelector={FLOATING_ELEMENT_SELECTOR}
      fullscreen={bowser.mobile}
    >
      <PanelGroup height="100%">
        <Panel corners="squared" align="middle" className="ws-translate-page-dialog__header">
          <DialogHeader>
            <DialogHeaderTitle>{captions.title}</DialogHeaderTitle>
          </DialogHeader>
        </Panel>
        <Panel className="ws-translate-page-dialog__body" height="100%" scrollable>
          <Box
            paddingTop="spacing-s"
            paddingBottom="spacing-s"
            paddingLeft="spacing-m"
            paddingRight="spacing-m"
          >
            <Stack gap="spacing-xs">
              <ControlGroup title={`${captions.fields.pages.label}:`}>
                <TreeSelectField
                  control={control}
                  name={fieldNames.TRANSLATE_PAGE_IDS}
                  items={pageItems}
                  size="small"
                  initialExpandedIds={initialExpandedPageItemIds}
                  placeholder={captions.fields.pages.placeholder}
                  searchPlaceholder={captions.fields.pages.searchPlaceholder}
                  selectAllLabel={captions.fields.pages.selectAllLabel}
                  noResultsText={captions.fields.pages.noResultsText}
                  getSelectionLabel={captions.fields.pages.getSelectionLabel}
                  rules={{
                    required: requiredValidation,
                  }}
                />
              </ControlGroup>
              <ControlGroup title={`${captions.fields.languages.label}:`}>
                <TreeSelectField
                  control={control}
                  name={fieldNames.TRANSLATE_LOCALES}
                  items={languageItems}
                  size="small"
                  withSelectAll={false}
                  disabled={languageItems.length === 0}
                  placeholder={captions.fields.languages.placeholder}
                  searchPlaceholder={captions.fields.languages.searchPlaceholder}
                  noResultsText={captions.fields.languages.noResultsText}
                  getSelectionLabel={captions.fields.languages.getSelectionLabel}
                  rules={{
                    required: requiredValidation,
                  }}
                />
                <Box marginTop="spacing-3-xs">
                  <Paragraph size="small" appearance="medium-emphasis">
                    {captions.fields.languages.notificationDescription}
                    {/* eslint-disable-next-line yola/jsx-a11y/anchor-is-valid */}
                    <Link isUnderlined href="/" onClick={onWebsiteSettingsClick}>
                      {captions.fields.languages.notificationPath}
                    </Link>
                    .
                  </Paragraph>
                </Box>
              </ControlGroup>
              {showOverrideExistingPages && (
                <ControlGroup
                  title={captions.fields.overrideExistingPages.title}
                  description={captions.fields.overrideExistingPages.description}
                >
                  <RadioButtonGroupField
                    name={fieldNames.EXISTING_PAGE_RESOLVING}
                    control={control}
                    options={overrideExistingPagesOptions}
                  />
                </ControlGroup>
              )}
              <HorizontalStack gap="spacing-3-xs" verticalAlign="center" align="space-between">
                <HorizontalStack gap="spacing-4-xs" verticalAlign="center">
                  <div className="ws-translate-page-dialog__checkbox-label">
                    <Paragraph weight="medium">{captions.fields.autoTranslate.label}</Paragraph>
                    <Icon glyph="ai" type="accent" />
                  </div>
                  <Tag size="small" appearance="cta-secondary" label="PRO" />
                </HorizontalStack>
                <ToggleField
                  name={fieldNames.TRANSLATE_AUTO_TRANS}
                  control={control}
                  size="small"
                />
              </HorizontalStack>
            </Stack>
          </Box>
        </Panel>
        <Panel className="ws-translate-page-dialog__footer">
          <DialogButtonGroup>
            <DialogButton onClick={onSubmit}>{captions.submit}</DialogButton>
            <DialogButtonGroupSeparator />
            <DialogButton onClick={onCancel}>{captions.cancel}</DialogButton>
          </DialogButtonGroup>
        </Panel>
      </PanelGroup>
    </Modal>
  );
}

TranslatePageDialog.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  languageItems: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  pageItems: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  overrideExistingPagesOptions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  captions: PropTypes.shape({
    title: PropTypes.string,
    fields: PropTypes.shape({
      pages: PropTypes.shape({
        label: PropTypes.string,
        placeholder: PropTypes.string,
        searchPlaceholder: PropTypes.string,
        selectAllLabel: PropTypes.string,
        noResultsText: PropTypes.string,
        getSelectionLabel: PropTypes.func,
      }),
      languages: PropTypes.shape({
        label: PropTypes.string,
        placeholder: PropTypes.string,
        searchPlaceholder: PropTypes.string,
        noResultsText: PropTypes.string,
        getSelectionLabel: PropTypes.func,
        notificationDescription: PropTypes.string,
        notificationPath: PropTypes.string,
      }),
      overrideExistingPages: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        radioButton1: PropTypes.string,
        radioButton2: PropTypes.string,
      }),
      autoTranslate: PropTypes.shape({
        label: PropTypes.string,
      }),
    }),
    submit: PropTypes.string,
    cancel: PropTypes.string,
  }).isRequired,
  control: PropTypes.shape({}).isRequired,
  showOverrideExistingPages: PropTypes.bool.isRequired,
  onWebsiteSettingsClick: PropTypes.func.isRequired,
  initialExpandedPageItemIds: PropTypes.arrayOf(PropTypes.string),
};

export default TranslatePageDialog;
